import React from 'react';

const safePct   = v => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
const META      = 90;
const colorSem  = v => v >= META ? '#28a745' : v >= 80 ? '#fd7e14' : '#dc3545';
const labelSem  = v => v >= META ? 'Cumple'  : v >= 80 ? 'En riesgo' : 'No cumple';
const labelTema = t => t === 'DOCENTE' ? 'Docente' : t === 'TUTOR' ? 'Tutor' : (t || '—');

// 0% casi siempre significa que el producto aún no tiene encuestas registradas
const sinDatos = v => v === 0;

const ResultadoCalidad = ({ calidad }) => {
  if (!calidad || calidad.length === 0)
    return <p className="text-muted mb-0">No hay datos de calidad para el período seleccionado.</p>;

  return (
    <>
      {calidad.map((proyecto, j) => {
        const items = proyecto.datos || [];

        // Promedio solo con items que tienen datos reales (> 0)
        const itemsConDatos = items.filter(d => safePct(d.porcentaje_mayor) > 0);
        const avgPct = itemsConDatos.length
          ? Math.round(itemsConDatos.reduce((s, d) => s + safePct(d.porcentaje_mayor), 0) / itemsConDatos.length)
          : null; // null = sin datos
        const col = avgPct !== null ? colorSem(avgPct) : '#aaa';

        return (
          <div key={j} className={j < calidad.length - 1 ? 'mb-4' : ''}>

            {/* ── Encabezado del proyecto ── */}
            <div
              className="d-flex align-items-center justify-content-between p-2 mb-3"
              style={{ backgroundColor: '#f8f9fa', borderRadius: 6, borderLeft: `4px solid ${col}` }}
            >
              <div>
                <div style={{ fontWeight: 700, fontSize: 14 }}>{proyecto.nombre_proyecto || 'Producto sin encuestas'}</div>
                {proyecto.fecha_inicio && proyecto.fecha_fin && (
                  <small className="text-muted">{proyecto.fecha_inicio} — {proyecto.fecha_fin}</small>
                )}
              </div>
              <div className="text-right">
                {avgPct !== null ? (
                  <>
                    <div style={{ fontWeight: 800, fontSize: 24, color: col, lineHeight: 1 }}>{avgPct}%</div>
                    <span style={{ fontSize: 11, fontWeight: 600, color: col, backgroundColor: `${col}15`, padding: '2px 10px', borderRadius: 20 }}>
                      {labelSem(avgPct)}
                    </span>
                  </>
                ) : (
                  <span style={{ fontSize: 12, color: '#aaa', fontStyle: 'italic' }}>Sin encuestas</span>
                )}
              </div>
            </div>

            {items.length > 0 ? (
              <>
                {/* ── Barras visuales Docente vs Tutor ── */}
                <div style={{ marginBottom: 16, padding: '12px 14px', background: '#fff', border: '1px solid #e9ecef', borderRadius: 8 }}>
                  {items.map((dato, i) => {
                    const v = safePct(dato.porcentaje_mayor);
                    const c = colorSem(v);
                    const brecha = parseFloat((Math.max(0, META - v)).toFixed(1));
                    const noData = sinDatos(v);

                    return (
                      <div key={i} style={{ marginBottom: i < items.length - 1 ? 14 : 0 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#555', minWidth: 60 }}>
                            {labelTema(dato.tema)}
                          </span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {noData ? (
                              <span style={{ fontSize: 11, color: '#aaa', fontStyle: 'italic' }}>Sin encuestas</span>
                            ) : (
                              <>
                                <span style={{ fontSize: 15, fontWeight: 800, color: c }}>{v}%</span>
                                <span style={{ fontSize: 10, padding: '1px 8px', borderRadius: 12, background: `${c}15`, color: c, fontWeight: 700 }}>
                                  {labelSem(v)}
                                </span>
                                {v < META && (
                                  <span style={{ fontSize: 10, color: '#aaa' }}>↓ {brecha}% de meta</span>
                                )}
                              </>
                            )}
                          </div>
                        </div>

                        {noData ? (
                          /* Placeholder gris cuando no hay encuestas */
                          <div style={{
                            height: 22, background: '#f8f9fa', borderRadius: 5,
                            border: '1px dashed #dee2e6',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                          }}>
                            <span style={{ fontSize: 10, color: '#bbb', fontStyle: 'italic' }}>Sin datos registrados</span>
                          </div>
                        ) : (
                          <div style={{ position: 'relative', height: 22, background: '#f0f3f7', borderRadius: 5 }}>
                            <div style={{
                              position: 'absolute', left: 0, top: 0,
                              height: '100%', width: `${Math.min(100, v)}%`,
                              background: c, borderRadius: 5,
                              transition: 'width .6s ease',
                              display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 8,
                            }}>
                              {v >= 20 && <span style={{ fontSize: 10, color: '#fff', fontWeight: 700 }}>{v}%</span>}
                            </div>
                            {/* Línea meta 90% */}
                            <div style={{ position: 'absolute', top: -3, left: `${META}%`, width: 2, height: 28, background: '#334155', opacity: 0.35, borderRadius: 1, zIndex: 2 }} />
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {itemsConDatos.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12 }}>
                      <div style={{ width: 14, height: 2, background: '#334155', opacity: 0.35, borderRadius: 1 }} />
                      <span style={{ fontSize: 10, color: '#aaa' }}>Línea de meta: {META}%</span>
                    </div>
                  )}
                </div>

                {/* ── Tabla de detalle ── */}
                <div className="table-responsive">
                  <table className="table table-bordered mb-0" style={{ fontSize: 12 }}>
                    <thead className="thead-light">
                      <tr>
                        <th>Sesión / Evaluado</th>
                        <th className="text-center" style={{ width: 70 }}>Meta</th>
                        <th className="text-center" style={{ width: 120 }}>Calificación</th>
                        <th className="text-center" style={{ width: 90 }}>Brecha</th>
                        <th className="text-center" style={{ width: 100 }}>Estado</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((dato, i) => {
                        const v      = safePct(dato.porcentaje_mayor);
                        const c      = colorSem(v);
                        const brecha = safePct(dato.brecha) || parseFloat((Math.max(0, META - v)).toFixed(1));
                        const noData = sinDatos(v);
                        return (
                          <tr key={i}>
                            <td className="align-middle" style={{ fontWeight: 600 }}>{labelTema(dato.tema)}</td>
                            <td className="text-center align-middle text-muted">{META}%</td>
                            <td className="text-center align-middle">
                              {noData ? (
                                <span style={{ fontSize: 11, color: '#aaa', fontStyle: 'italic' }}>Sin datos</span>
                              ) : (
                                <>
                                  <div style={{ fontWeight: 700, fontSize: 15, color: c }}>{v}%</div>
                                  <div className="progress mt-1" style={{ height: 3, borderRadius: 2 }}>
                                    <div className="progress-bar" style={{ width: `${Math.min(100, v)}%`, backgroundColor: c }} />
                                  </div>
                                </>
                              )}
                            </td>
                            <td className="text-center align-middle text-muted">
                              {noData ? '—' : `${brecha}%`}
                            </td>
                            <td className="text-center align-middle">
                              {noData ? (
                                <span style={{ fontSize: 11, color: '#aaa', backgroundColor: '#f8f9fa', padding: '2px 8px', borderRadius: 20 }}>
                                  Sin datos
                                </span>
                              ) : (
                                <span style={{ fontSize: 11, fontWeight: 600, color: c, backgroundColor: `${c}15`, padding: '2px 8px', borderRadius: 20 }}>
                                  {labelSem(v)}
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            ) : (
              <p className="text-muted mb-0" style={{ fontSize: 13, padding: '8px 0' }}>Sin encuestas registradas para este producto.</p>
            )}
          </div>
        );
      })}
    </>
  );
};

export default ResultadoCalidad;
